import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';

export default function MenuManagement() {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Form state
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [category, setCategory] = useState('Food');
    const [price, setPrice] = useState(0);
    const [image, setImage] = useState('');
    const [description, setDescription] = useState('');
    const [isAvailable, setIsAvailable] = useState(true);

    const categories = ['Drinks', 'Food', 'Appetizers', 'Main Course', 'Desserts', 'Cocktails', 'Beer', 'Wine', 'Spirits', 'Soft Drinks', 'Snacks'];

    const fetchMenu = async () => {
        try {
            setLoading(true);
            const params = {};
            if (userInfo && userInfo.restaurantId) {
                params.restaurantId = userInfo.restaurantId;
            }
            const { data } = await axios.get(`/api/menu`, { params });
            setMenuItems(data);
            setLoading(false);
        } catch (err) {
            toast.error(getError(err));
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMenu();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!userInfo || !userInfo.restaurantId) {
            toast.error('No Restaurant ID found. Please sign out and sign in again to refresh your session.');
            return;
        }

        try {
            const payload = {
                name,
                slug,
                category,
                price,
                image,
                description,
                isAvailable,
                restaurantId: userInfo.restaurantId // Pass restaurant ID
            };

            if (editingItem) {
                await axios.put(
                    `/api/menu/${editingItem._id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success('Menu item updated');
            } else {
                await axios.post(
                    '/api/menu',
                    payload,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success('Menu item created');
            }
            setShowModal(false);
            fetchMenu();
        } catch (err) {
            toast.error(getError(err));
        }
    };

    const deleteHandler = async (item) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                await axios.delete(`/api/menu/${item._id}`, {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                toast.success('Item deleted');
                fetchMenu();
            } catch (err) {
                toast.error(getError(err));
            }
        }
    };

    const openModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setName(item.name);
            setSlug(item.slug);
            setCategory(item.category);
            setPrice(item.price);
            setImage(item.image);
            setDescription(item.description);
            setIsAvailable(item.isAvailable);
        } else {
            setEditingItem(null);
            setName('');
            setSlug('');
            setCategory('Food');
            setPrice(0);
            setImage('/images/p1.jpg'); // Default placeholder
            setDescription('');
            setIsAvailable(true);
        }
        setShowModal(true);
    };

    // Auto-generate slug from name
    const handleNameChange = (e) => {
        const val = e.target.value;
        setName(val);
        if (!editingItem) {
            setSlug(val.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, ''));
        }
    };

    return (
        <Container className="py-4">
            <Helmet>
                <title>Menu Management</title>
            </Helmet>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Menu Management</h2>
                <Button onClick={() => openModal()} variant="primary">
                    <i className="fas fa-plus me-2"></i> Add Item
                </Button>
            </div>

            {loading ? (
                <div>Loading menu items...</div>
            ) : (
                <Table responsive striped bordered hover>
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th>Availability</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {menuItems.map((item) => (
                            <tr key={item._id}>
                                <td>
                                    <img src={item.image} alt={item.name} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                </td>
                                <td>{item.name}</td>
                                <td>{item.category}</td>
                                <td>{item.price} RWF</td>
                                <td>
                                    {item.isAvailable ? (
                                        <span className="badge bg-success">Available</span>
                                    ) : (
                                        <span className="badge bg-danger">Unavailable</span>
                                    )}
                                </td>
                                <td>
                                    <Button variant="light" size="sm" className="me-2" onClick={() => openModal(item)}>
                                        <i className="fas fa-edit"></i>
                                    </Button>
                                    <Button variant="light" size="sm" className="text-danger" onClick={() => deleteHandler(item)}>
                                        <i className="fas fa-trash"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        required
                                        value={name}
                                        onChange={handleNameChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Slug</Form.Label>
                                    <Form.Control
                                        type="text"
                                        required
                                        value={slug}
                                        onChange={(e) => setSlug(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Category</Form.Label>
                                    <Form.Select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                    >
                                        {categories.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Price (RWF)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        required
                                        value={price}
                                        onChange={(e) => setPrice(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        required
                                        value={image}
                                        readOnly
                                        placeholder="Image URL will be generated after upload"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Upload Image</Form.Label>
                                    <Form.Control 
                                        type="file" 
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            
                                            // Basic validation
                                            if (!file.type.startsWith('image/')) {
                                                toast.error('Please select an image file');
                                                return;
                                            }

                                            try {
                                                const bodyFormData = new FormData();
                                                bodyFormData.append('file', file);
                                                toast.info('Uploading image...');
                                                const { data } = await axios.post('/api/upload', bodyFormData, {
                                                    headers: {
                                                        Authorization: `Bearer ${userInfo.token}`,
                                                    },
                                                });
                                                setImage(data.secure_url);
                                                toast.success('Image uploaded successfully');
                                            } catch (err) {
                                                console.error("Upload error:", err);
                                                toast.error(getError(err));
                                            }
                                        }} 
                                    />
                                    <div className="mt-2 text-center border p-2 bg-light rounded" style={{ minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {image ? (
                                            <img src={image} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '4px' }} />
                                        ) : (
                                            <span className="text-muted small">No image selected</span>
                                        )}
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="availability-switch"
                                        label="Available for ordering"
                                        checked={isAvailable}
                                        onChange={(e) => setIsAvailable(e.target.checked)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                required
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Form.Group>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            Save Item
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
}
